import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import { getRequest, postRequest, patchRequest, deleteRequest, server } from "../config";

const CategoryGroups = () => {
  const [groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form fields according to the schema provided
  const [groupId, setGroupId] = useState("");
  const [name, setName] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [status, setStatus] = useState(true);

  useEffect(() => {
    fetchGroups();
    fetchCategories();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await getRequest(`${server.uri}category-groups`);
      if (res) {
        setGroups(res);
        setAllGroups(res);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getRequest(`${server.uri}categories`);
      if (res) {
        setCategories(res);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const resetForm = () => {
    setGroupId("");
    setName("");
    setSelectedCategories([]);
    setStatus(true);
  };

  const handleAddGroup = () => {
    resetForm();
    setEditingGroup(null);
    setOpenDialog(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupId(group.groupId || "");
    setName(group.name || "");
    setSelectedCategories(group.categories || []);
    setStatus(group.status ?? true);
    setOpenDialog(true);
  };

  const handleDeleteGroup = async (id) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      await deleteRequest(`${server.uri}category-groups/${id}`);
      fetchGroups();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveGroup = async () => {
    const payload = {
    //   groupId: groupId || `${Date.now()}`,
      name,
      categories: selectedCategories,
      status
    };

    if (editingGroup) {
      await patchRequest(`${server.uri}category-groups/${editingGroup.groupId}`, payload);
    } else {
      await postRequest(`${server.uri}category-groups`, payload);
    }

    fetchGroups();
    handleDialogClose();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = allGroups.filter((group) =>
      (group.name || "").toLowerCase().includes(value)
    );
    setGroups(filtered);
  };

  const handleCategorySelection = (event) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginTop: 8, marginBottom: 2 }}>
        Category Groups
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search by group name"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ color: "gray", marginRight: 1 }} />,
          }}
          sx={{ width: "60%" }}
        />
        <Button variant="contained" onClick={handleAddGroup}>
          Add Category Group
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
              <TableRow>
                <TableCell><strong>Group Name</strong></TableCell>
                <TableCell><strong>Categories</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.groupId}>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {group.categories && group.categories.length > 0 ? (
                        group.categories.map((categoryId, index) => {
                          const category = categories.find(cat => cat.categoryId === categoryId);
                          return (
                            <Chip
                              key={index}
                              label={category?.name || categoryId}
                              size="small"
                              variant="outlined"
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="textSecondary">No categories</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{group.status ? "Active" : "Inactive"}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditGroup(group)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteGroup(group.groupId)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No category groups found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? "Edit Category Group" : "Add New Category Group"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={selectedCategories}
              onChange={handleCategorySelection}
              label="Categories"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const category = categories.find(cat => cat.categoryId === value);
                    return (
                      <Chip key={value} label={category?.name || value} size="small" />
                    );
                  })}
                </Box>
              )}
            >
              {categories.map((category) => (
                <MenuItem key={category.categoryId} value={category.categoryId}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={status} onChange={(e) => setStatus(e.target.checked)} />}
            label={status ? "Active" : "Inactive"}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained">
            {editingGroup ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CategoryGroups;